import type { CSSProperties, ReactNode } from "react";
import React, { useState } from "react";
import { touch } from "redux-form";
import cs from "classnames";

import type { OptionBase } from "@braintrust/braintrust-ui-components";
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@braintrust/braintrust-ui-components";
import type { TypedWrappedFieldProps } from "@js/forms/utils";

import type { TextFieldProps } from "..";

import styles from "./style.module.scss";

const CustomRadioButton = ({
  active,
  error,
}: {
  active?: boolean;
  error?: boolean;
}) => {
  return (
    <div
      className={cs(styles.customRadioButton, {
        [styles.customRadioButtonActive]: active,
        [styles.customRadioButtonError]: error,
      })}
    >
      <div
        className={cs(styles.customRadioButtonCircle, {
          [styles.customRadioButtonCircleActive]: active,
        })}
      />
    </div>
  );
};

type RadioListFieldProps<
  TValue,
  TOption extends OptionBase<TValue> = OptionBase<TValue>,
> = TypedWrappedFieldProps<NoInfer<TValue> | ""> &
  Omit<TextFieldProps, "input" | "label"> & {
    options: TOption[];
    label?: string;
    borderRadio?: boolean;
    adornment?: NoInfer<(option: TOption["value"]) => ReactNode>;
    yellowOutline?: boolean;
    errorStyle?: CSSProperties;
    radioOptionClassName?: string;
  };

export const RadioListField = <
  TValue,
  TOption extends OptionBase<TValue> = OptionBase<TValue>,
>(
  props: RadioListFieldProps<TValue, TOption>,
) => {
  const {
    input: { name, value, onChange },
    meta: { form, error, touched, dispatch },
  } = props;

  const handleChange = (newValue: TValue | "") => {
    // If checked already checked element then it means I want to uncheck it.
    dispatch(touch(form, name));
    onChange(newValue === value ? "" : newValue);
  };

  return (
    <RadioListFieldComponent<TValue | "", TOption>
      {...props}
      onChange={handleChange}
      value={value}
      name={name}
      error={!!error && touched ? error : undefined}
    />
  );
};

type RadioListFieldComponentProps<
  TValue,
  TOption extends OptionBase<TValue> = OptionBase<TValue>,
> = Omit<TextFieldProps, "input" | "label" | "error" | "onChange" | "meta"> & {
  options: TOption[];
  label?: string;
  borderRadio?: boolean;
  adornment?: NoInfer<(option: TOption["value"]) => ReactNode>;
  yellowOutline?: boolean;
  errorStyle?: CSSProperties;
  radioOptionClassName?: string;
  name?: string;
  value: TValue | null;
  onChange: (value: TValue) => void;
  error?: string;
};

export const RadioListFieldComponent = <
  TValue,
  TOption extends OptionBase<TValue> = OptionBase<TValue>,
>(
  props: RadioListFieldComponentProps<TValue, TOption>,
) => {
  const {
    options = [],
    label,
    name,
    value,
    onChange,
    error,
    style,
    borderRadio = true,
    adornment,
    yellowOutline,
    errorStyle,
    disabled = false,
    radioOptionClassName,
  } = props;
  const [defaultValue] = useState(value);

  const handleChange = (newValue: TValue) => {
    onChange(newValue);
  };

  return (
    <FormControl sx={style} component="fieldset">
      {error && (
        <div
          style={{ ...errorStyle }}
          className="general-form-error general-form-error__align-left"
        >
          {error}
        </div>
      )}
      <RadioGroup defaultValue={defaultValue} aria-label={label} name={name}>
        {options.map((option) => {
          const valueToCompare = JSON.stringify(option.value);
          const isChecked = valueToCompare === JSON.stringify(value);

          return (
            <Box sx={{ mb: 2 }} key={valueToCompare}>
              <FormControlLabel
                className={cs(radioOptionClassName, {
                  [styles.formControlLabelChosenField]:
                    isChecked && borderRadio,
                  [styles.formControlLabelChosenFieldNoBorder]:
                    isChecked && !borderRadio,
                  [styles.formControlLabelBorder]: borderRadio,
                })}
                classes={{
                  label: cs(styles.formControlLabel, {
                    [styles.formControlLabelError]: !!error,
                  }),
                }}
                value={option.value}
                onChange={() => handleChange(option.value)}
                control={
                  <Radio
                    icon={<CustomRadioButton active={false} error={!!error} />}
                    checkedIcon={<CustomRadioButton active={true} />}
                    checked={isChecked}
                    classes={{
                      checked: cs({
                        [styles.formControlLabelYellowChecked]: yellowOutline,
                      }),
                    }}
                  />
                }
                label={<Typography component="span">{option.label}</Typography>}
                disabled={disabled}
              />
              {adornment && adornment(option.value)}
            </Box>
          );
        })}
      </RadioGroup>
    </FormControl>
  );
};
